import moment from "moment";
import {useEffect, useState} from "react";

import {AutoLayoutClientJobState} from "modules/auto_layout/services/AutoLayoutStoreService";

import {useAutoLayoutStateWithSelector} from "./useAutoLayoutState";

/**
 * Hook to get the current auto-layout job duration, updating every second.
 * @returns The humanized duration string or undefined if no active job with startTime exists.
 */
export function useCurrentAutoLayoutJobDuration(): string | undefined {
    const {startTime, state} = useAutoLayoutStateWithSelector((state) => ({
        startTime: state.currentJob?.startTime,
        state: state.currentJob?.state,
    }));
    const [duration, setDuration] = useState<string | undefined>(undefined);

    useEffect(() => {
        if (!state) {
            setDuration(undefined);
            return;
        }

        // If the job is in a terminal state, do not update duration
        // But also - don't clear it, so we keep showing the final duration
        if (
            [
                AutoLayoutClientJobState.applied,
                AutoLayoutClientJobState.canceled,
                AutoLayoutClientJobState.canceling,
                AutoLayoutClientJobState.complete,
                AutoLayoutClientJobState.error,
            ].includes(state)
        ) {
            return;
        }

        const updateDuration = () => {
            const now = moment();
            const start = moment(startTime);
            const diff = moment.duration(now.diff(start));
            setDuration(diff.humanize());
        };

        updateDuration();

        const interval = setInterval(updateDuration, 1000);

        return () => {
            clearInterval(interval);
        };
    }, [startTime, state]);

    return duration;
}
